/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, Suspense, useState } from 'react'

import { EuiButton, EuiLink, EuiToolTip } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import usePermissions from '@modules/permissions-lib/hooks'
import type { MouseClickEvent } from '@modules/cui/Button/types'
import { useConfig } from '@modules/cui/ConfigContext'
import Feature from '@modules/utils/feature'

import CustomerOnlyAction from '@/components/User/CustomerOnlyAction'
import { isFedRampHigh } from '@/lib/fedramp-high'

import type { Props } from './types'

// We use lazy loading here to avoid loading recurly and all dependencies if CC flow is disabled (e.g. GovCloud or Admin Console)
const CreditCardModal = React.lazy(() => import(`../CreditCardModal`))

const CreditCardModalButton = ({
  onCloseModal,
  onOpenModal,
  disabled,
  tooltipMessage,
  fill,
  type,
  children,
  onSendBillingDetailsSuccess,
  paymentDetails,
  buttonProps,
}: Props) => {
  const [modalOpen, setModalOpen] = useState(false)
  const { hasPermissions } = usePermissions([{ type: 'billing-organization', action: 'create' }]) // Billing Admin only
  const isGovCloud = Boolean(useConfig(Feature.hideIrrelevantSectionsFromGovCloud))

  const onClick = (e: MouseClickEvent): void => {
    setModalOpen(true)
    onOpenModal?.()

    e.preventDefault()
  }

  const handleCloseModal = (): void => {
    setModalOpen(false)
    onCloseModal?.()
  }

  const renderButton = () => {
    const Button = type === `full` ? EuiButton : EuiLink

    return (
      <Button
        disabled={disabled || !hasPermissions || isGovCloud || isFedRampHigh()}
        fill={fill}
        data-test-id='credit-card-modal-button'
        onClick={onClick}
        {...buttonProps}
        // @ts-ignore EuiButton and EuiLink interfaces are incompatible, ideally we should figure out a way to use a common interface
        color={buttonProps?.color}
      >
        {children}
      </Button>
    )
  }

  // Credit card flow is disabled in Govcloud/Fedramp and if you dont have permissions
  if (!hasPermissions || isGovCloud || isFedRampHigh() || tooltipMessage) {
    return (
      <EuiToolTip
        content={
          tooltipMessage ?? (
            <FormattedMessage
              id='credit-card-button.permissions-message'
              defaultMessage='You are not authorized to perform this action.'
            />
          )
        }
      >
        {renderButton()}
      </EuiToolTip>
    )
  }

  return (
    <Fragment>
      {modalOpen && (
        <Suspense fallback={null}>
          <CreditCardModal
            closeModal={handleCloseModal}
            onSendBillingDetailsSuccess={onSendBillingDetailsSuccess}
            paymentDetails={paymentDetails}
          />
        </Suspense>
      )}
      <CustomerOnlyAction>{renderButton()}</CustomerOnlyAction>
    </Fragment>
  )
}

export default CreditCardModalButton
